


// public/src/pages/ServiceDetail.jsx - 
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiCheck,
  FiClock,
  FiUsers,
  FiTarget,
  FiAward,
  FiGlobe,
  FiMail,
  FiPhone,
} from "react-icons/fi";
import axios from "axios";

function ServiceDetail() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchService();
  }, [id]);

  const fetchService = async () => {
    try {
      const response = await axios.get(
        `https://agri-back-mauve.vercel.app/api/content/public/service/${id}`
      );
      if (response.data.success) {
        setService(response.data.data);
      } else {
        setError("Service not found");
      }
    } catch (err) {
      setError("Failed to load service details");
      console.error("Error fetching service:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Service Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <Link
            to="/services"
            className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
          >
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  // Handle nested service data structure
  const serviceData = service.service || service;

  return (
    <div className="service-detail-page min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="page-hero bg-gradient-to-r from-green-900 to-emerald-800 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-6xl mx-auto"
          >
            <Link 
              to="/services" 
              className="back-link inline-flex items-center gap-2 text-green-200 hover:text-white mb-6 transition-colors duration-300"
            >
              <FiArrowLeft className="w-4 h-4" />
              Back to Services
            </Link>
            
            <div className="flex flex-col lg:flex-row items-start justify-between gap-8">
              <div className="flex-1">
                <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                  {service.title}
                </h1>
                <p className="text-xl text-green-200 leading-relaxed">
                  {service.description}
                </p>
              </div>
              
              <div className="service-icon text-6xl lg:text-8xl opacity-80">
                {serviceData.icon === "precision" && "🌱"}
                {serviceData.icon === "iot" && "📡"}
                {serviceData.icon === "data" && "📊"}
                {serviceData.icon === "drone" && "🚁"}
                {serviceData.icon === "monitoring" && "👁️"}
                {serviceData.icon === "analysis" && "🔍"}
                {serviceData.icon === "consulting" && "💼"}
                {!serviceData.icon && "🌾"}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="main-content py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content Column */}
              <div className="lg:col-span-2 space-y-12">
                {/* Service Overview */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="service-overview bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8"
                >
                  <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-3">
                    <FiTarget className="text-green-500" />
                    Service Overview
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-6">
                    {service.description}
                  </p>
                  
                  {service.content && (
                    <div className="prose prose-lg dark:prose-invert max-w-none">
                      <p className="text-gray-600 dark:text-gray-300">
                        {service.content}
                      </p>
                    </div>
                  )}
                </motion.div>

                {/* Key Features */}
                {serviceData.features && serviceData.features.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="features-section bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8"
                  >
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-3">
                      <FiCheck className="text-green-500" />
                      Key Features
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {serviceData.features.map((feature, index) => (
                        <div key={index} className="feature-item flex items-start gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                          <FiCheck className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Benefits */}
                {serviceData.benefits && serviceData.benefits.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="benefits-section bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8"
                  >
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-3">
                      <FiAward className="text-green-500" />
                      Key Benefits
                    </h3>
                    <ul className="benefits-list space-y-3">
                      {serviceData.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3 text-gray-600 dark:text-gray-300">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-lg">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                {/* Technologies */}
                {serviceData.technologies && serviceData.technologies.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="technologies-section bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8"
                  >
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-3">
                      <FiGlobe className="text-green-500" />
                      Technologies & Methods
                    </h3>
                    <div className="technologies-tags flex flex-wrap gap-3">
                      {serviceData.technologies.map((tech, index) => (
                        <span 
                          key={index} 
                          className="tech-tag px-4 py-2 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full text-sm font-medium border border-green-200 dark:border-green-700"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Sidebar Column */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="service-sidebar space-y-6"
              >
                {/* Service Details Card */}
                <div className="sidebar-card bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sticky top-6">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                    Service Details
                  </h3>

                  <div className="space-y-4">
                    <div className="detail-item flex items-start gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <FiClock className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-gray-700 dark:text-gray-300 block">Duration</strong>
                        <p className="text-gray-600 dark:text-gray-400">{serviceData.duration || "Custom timeline"}</p>
                      </div>
                    </div>

                    <div className="detail-item flex items-start gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <FiUsers className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-gray-700 dark:text-gray-300 block">Service Category</strong>
                        <p className="text-gray-600 dark:text-gray-400">{service.category}</p>
                      </div>
                    </div>

                    {serviceData.deliveryMethod && (
                      <div className="detail-item flex items-start gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                        <FiTarget className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong className="text-gray-700 dark:text-gray-300 block">Delivery Method</strong>
                          <p className="text-gray-600 dark:text-gray-400">{serviceData.deliveryMethod}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Support Options */}
                  {serviceData.support && serviceData.support.length > 0 && (
                    <div className="support-section mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Support Includes:</h4>
                      <ul className="space-y-2">
                        {serviceData.support.map((support, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <FiCheck className="w-4 h-4 text-green-500" />
                            {support}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CTA Button */}
                  <div className="cta-section mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Link 
                      to="/contact" 
                      className="cta-button w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-center py-4 rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl block"
                    >
                      Get Started Today
                    </Link>
                    <p className="text-center text-gray-500 dark:text-gray-400 text-sm mt-3">
                      Free initial consultation
                    </p>
                  </div>
                </div>

                {/* Contact Card */}
                <div className="contact-card bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                  <h4 className="font-bold text-gray-800 dark:text-white mb-4">Need Immediate Assistance?</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                      <FiMail className="w-4 h-4 text-green-500" />
                      <span>contact@afrikonsults.com</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                      <FiPhone className="w-4 h-4 text-green-500" />
                      <span>+254 700 000000</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services CTA */}
      <section className="related-cta py-16 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
              Explore Our Other Services
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg mb-8">
              We offer a comprehensive range of agricultural consulting services to meet all your needs.
            </p>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              View All Services
              <FiArrowLeft className="w-5 h-5 transform rotate-180" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ServiceDetail;

